// Компонент для отображения индикатора риска
import { AlertTriangle, CheckCircle, XCircle, Info } from 'lucide-react'

export type RiskLevel = 'none' | 'low' | 'medium' | 'high' | 'error' | 'pending'

interface RiskBadgeProps {
  level: RiskLevel
  label?: string
  size?: 'sm' | 'md' | 'lg'
  showIcon?: boolean
  className?: string
}

export const RiskBadge: React.FC<RiskBadgeProps> = ({
  level,
  label,
  size = 'md',
  showIcon = true,
  className = ''
}) => {
  const config = {
    none: {
      bg: 'bg-green-100',
      text: 'text-green-800',
      border: 'border-green-200',
      icon: <CheckCircle className="text-green-600" />,
      defaultLabel: 'Нет рисков'
    },
    low: {
      bg: 'bg-blue-100',
      text: 'text-blue-800',
      border: 'border-blue-200',
      icon: <Info className="text-blue-600" />,
      defaultLabel: 'Низкий риск'
    },
    medium: {
      bg: 'bg-yellow-100',
      text: 'text-yellow-800',
      border: 'border-yellow-200',
      icon: <AlertTriangle className="text-yellow-600" />,
      defaultLabel: 'Средний риск'
    },
    high: {
      bg: 'bg-red-100',
      text: 'text-red-800',
      border: 'border-red-200',
      icon: <AlertTriangle className="text-red-600" />,
      defaultLabel: 'Высокий риск'
    },
    error: {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      border: 'border-gray-200',
      icon: <XCircle className="text-gray-600" />,
      defaultLabel: 'Ошибка'
    },
    pending: {
      bg: 'bg-gray-100',
      text: 'text-gray-800',
      border: 'border-gray-200',
      icon: <Info className="text-gray-600" />,
      defaultLabel: 'Не проверено'
    }
  }

  const { bg, text, border, icon, defaultLabel } = config[level]

  const sizeClasses = {
    sm: 'px-2 py-1 text-xs',
    md: 'px-3 py-1.5 text-sm',
    lg: 'px-4 py-2 text-base'
  }

  return (
    <div
      className={`
        inline-flex items-center gap-2 rounded-full border ${bg} ${text} ${border} ${sizeClasses[size]}
        ${className}
      `}
    >
      {showIcon && <span className="flex-shrink-0">{icon}</span>}
      <span className="font-medium">{label || defaultLabel}</span>
    </div>
  )
}

// Вспомогательный компонент для отображения риска на основе оценки
interface ComplianceScoreBadgeProps {
  score: number // 0.0 - 1.0
  showScore?: boolean
  size?: 'sm' | 'md' | 'lg'
}

export const ComplianceScoreBadge: React.FC<ComplianceScoreBadgeProps> = ({
  score,
  showScore = true,
  size = 'md'
}) => {
  let level: RiskLevel = 'pending'
  let label = ''

  if (score >= 0.9) {
    level = 'none'
    label = showScore ? `${Math.round(score * 100)}%` : 'Отлично'
  } else if (score >= 0.7) {
    level = 'low'
    label = showScore ? `${Math.round(score * 100)}%` : 'Хорошо'
  } else if (score >= 0.5) {
    level = 'medium'
    label = showScore ? `${Math.round(score * 100)}%` : 'Средне'
  } else {
    level = 'high'
    label = showScore ? `${Math.round(score * 100)}%` : 'Критично'
  }

  return <RiskBadge level={level} label={label} size={size} />
}

// Компонент для отображения списка рисков
interface RiskListProps {
  risks: Array<{
    type: string
    severity: 'низкий' | 'средний' | 'высокий'
    description: string
  }>
  maxItems?: number
}

export const RiskList: React.FC<RiskListProps> = ({ risks, maxItems = 3 }) => {
  const displayedRisks = risks.slice(0, maxItems)
  const remaining = risks.length - maxItems

  const getSeverityLevel = (severity: string): RiskLevel => {
    switch (severity) {
      case 'высокий': return 'high'
      case 'средний': return 'medium'
      case 'низкий': return 'low'
      default: return 'low'
    }
  }

  return (
    <div className="space-y-2">
      {displayedRisks.map((risk, index) => (
        <div key={index} className="flex items-start gap-2">
          <div className="mt-0.5">
            <RiskBadge level={getSeverityLevel(risk.severity)} size="sm" showIcon={false} />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-gray-900">{risk.type}</p>
            <p className="text-sm text-gray-600">{risk.description}</p>
          </div>
        </div>
      ))}
      {remaining > 0 && (
        <p className="text-sm text-gray-500">+{remaining} других рисков</p>
      )}
    </div>
  )
}